import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Wallet, ArrowDownCircle, ArrowUpCircle, Download, Loader2 } from "lucide-react";
import api from "../api";

const money = (v) => `KES ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function WalletPage() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/payments/wallet/summary/"),
        api.get("/payments/wallet/transactions/"),
      ]);
      setSummary(s.data);
      setPhone(s.data?.payout_phone || "");
      setTxns(t.data?.transactions || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const submitWithdrawal = async () => {
    const amt = parseFloat(amount);
    if (!(amt > 0)) return;
    setBusy(true);
    try {
      await api.post("/payments/wallet/withdrawals/", { amount: amt, phone });
      setShowWithdraw(false); setAmount("");
      flash("Withdrawal requested — an admin will process it.");
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Couldn't request the withdrawal.");
    } finally { setBusy(false); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/payments/wallet/transactions/?format=csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "eventsawa-wallet-transactions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { flash("Couldn't download the report."); }
  };

  if (loading) {
    return <div className="min-h-screen flex justify-center items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }
  const s = summary || {};
  const pct = Math.round((s.commission_rate || 0.05) * 100);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <Helmet><title>My Wallet | EventSawa</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        <div className="flex items-center gap-2">
          <Wallet className="h-6 w-6 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Wallet</h1>
        </div>

        {msg && <div className="rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 text-sm">{msg}</div>}

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">Withdrawable balance</p>
            <p className="text-4xl font-bold text-gray-900 dark:text-white mt-1">{money(s.withdrawable)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From M-Pesa payments, after {pct}% platform fee</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowWithdraw(true)} disabled={!(s.withdrawable > 0)}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                Request withdrawal
              </button>
              <button onClick={downloadReport}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1">
                <Download size={16} /> Download report
              </button>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6 space-y-2 text-sm">
            <Row label="M-Pesa collected" value={money(s.gross_platform)} />
            <Row label={`Platform fee (${pct}%)`} value={`− ${money(s.commission)}`} />
            <Row label="Already withdrawn" value={`− ${money(s.withdrawn)}`} />
            {s.reserved > 0 && <Row label="Pending withdrawals" value={`− ${money(s.reserved)}`} />}
            <hr className="border-gray-100 dark:border-gray-700" />
            <Row label="Collected directly (offline)" value={money(s.offline_collected)} muted />
            {s.pending_platform > 0 && <Row label="Awaiting M-Pesa clearing" value={money(s.pending_platform)} muted />}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Transactions</h2>
          {txns.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No wallet transactions yet.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {txns.map((t, i) => {
                const income = (t.type || "in") === "in";
                return (
                  <div key={i} className="flex items-center gap-3 py-3">
                    {income ? <ArrowDownCircle className="text-green-600" size={22} /> : <ArrowUpCircle className="text-amber-600" size={22} />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">{t.direction}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {[t.method, t.party, t.event, (t.date || "").slice(0, 10)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${income ? "text-green-600" : "text-amber-600"}`}>{income ? "+" : "−"} {t.amount}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{t.status}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showWithdraw && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Request withdrawal</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Available: {money(s.withdrawable)}. Paid to the M-Pesa number below — confirm it's correct.
            </p>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (KES)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="M-Pesa number"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <div className="flex gap-2">
              <button onClick={() => setShowWithdraw(false)} className="flex-1 py-2 border rounded-md text-gray-700 dark:text-gray-200">Cancel</button>
              <button onClick={submitWithdrawal} disabled={busy || !(parseFloat(amount) > 0) || parseFloat(amount) > s.withdrawable || !phone}
                className="flex-1 py-2 rounded-md bg-blue-600 text-white font-medium disabled:opacity-50">
                {busy ? "Requesting…" : "Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className={muted ? "text-gray-500 dark:text-gray-400" : "font-medium text-gray-900 dark:text-white"}>{value}</span>
    </div>
  );
}
